import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { ownerStamp } from '../recordOwner';
import HeaderActionsPortal from './HeaderActionsPortal';
import ModuleEditPermissions from './ModuleEditPermissions';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';
import { Box, Plus, Trash2, CheckCircle, XCircle, RefreshCw, ClipboardList, Edit2, Download, Upload, X, Shield } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import Papa from 'papaparse';

const InventoryAdmin = () => {
  const { t } = useT();
  const slug = (v) => String(v || '').toLowerCase().replace(/[^a-z]/g, '');
  // Item statuses share their keys with My Inventory, so the two screens cannot drift apart.
  const statusWord = (v) => t(`inventoryUser.statuses.${slug(v)}`, {}, v);
  const appStatusWord = (v) => t(`inventoryAdmin.appStatuses.${slug(v)}`, {}, v);
  const appTypeWord = (v) => t(`inventoryAdmin.appTypes.${slug(v)}`, {}, v);
  const [activeTab, setActiveTab] = useState('inventory');
  const [inventory, setInventory] = useState([]);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form State
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newItem, setNewItem] = useState({ id: '', name: '', category: '', quantity: 1, assignedTo: '', location: '', status: 'Active', unitCost: 0, fundingSource: '', committee: '' });
  const [dropdownConfig, setDropdownConfig] = useState({ sourceOfFunding: [], inventoryCommittee: [] });
  const { activeSchoolId, isMobile, closeSidebar } = useOutletContext();

  const resetForm = () => {
    setShowAddForm(false);
    setEditingId(null);
    setNewItem({ id: '', name: '', category: '', quantity: 1, assignedTo: '', location: '', status: 'Active', unitCost: 0, fundingSource: '', committee: '' });
  };

  // On mobile the sidebar is a fixed overlay, so it would cover the dialog. Retract it whenever the
  // form opens -- covers the Add button and the per-row Edit action alike.
  useEffect(() => {
    if (showAddForm && isMobile) closeSidebar?.();
  }, [showAddForm, isMobile, closeSidebar]);

  // Escape closes the dialog, and while it is open the page behind must not scroll -- otherwise
  // flicking the backdrop on a phone scrolls the inventory list underneath the form.
  useEffect(() => {
    if (!showAddForm) return;
    const onKey = (e) => { if (e.key === 'Escape') resetForm(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showAddForm]);

  useEffect(() => {
    if (activeSchoolId) fetchData();
    const docRef = activeSchoolId 
      ? doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings')
      : doc(db, 'metadata', 'system_settings');
      
    const unsub = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists() && docSnap.data().dropdowns) {
        const d = docSnap.data().dropdowns;
        setDropdownConfig({
          ...d,
          inventoryCommittee: d.inventoryCommittee || ['IT Committee', 'Science Committee', 'Sports Committee']
        });
      }
    });
    return () => unsub();
  }, [activeSchoolId]);

  const fetchData = async () => {
    if (!activeSchoolId) return;
    setLoading(true);
    try {
      // Fetch Users and Staff Profiles for assignment dropdown
      const staffSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'staff_profiles')));
      const uList = [];
      const uMap = {};
      
      staffSnap.forEach(d => {
        const uData = d.data();
        if (d.id !== 'admin_config') {
          const label = uData.initial || d.id;
          uList.push({ accountId: d.id, label, initial: uData.initial || d.id });
          uMap[d.id] = label;
        }
      });
      // Firestore returns documents ordered by ID, and these IDs are emails -- so a person shows up
      // at their email's position while the dropdown displays their initial (test@... sat among the
      // T's while reading "BBB"). Sort by what is actually on screen.
      uList.sort((a, b) => String(a.label).localeCompare(String(b.label)));
      setUsers(uList);
      setUserMap(uMap);

      // Fetch Inventory
      const iSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'inventory')));
      const iList = [];
      iSnap.forEach(d => iList.push({ uid: d.id, ...d.data() })); // uid is firestore doc id, id is custom format
      setInventory(iList);

      // Fetch Applications
      const aSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'inventory_applications')));
      const aList = [];
      aSnap.forEach(d => aList.push({ uid: d.id, ...d.data() }));
      aList.sort((a, b) => (b.requestDate?.seconds || 0) - (a.requestDate?.seconds || 0));
      setApplications(aList);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.name) return;
    try {
      const finalId = newItem.id || `INV-${Date.now().toString().slice(-6)}`;
      const payload = {
        ...newItem,
        id: finalId,
        quantity: Number(newItem.quantity) || 1,
        unitCost: Number(newItem.unitCost) || 0,
        totalCost: (Number(newItem.quantity) || 1) * (Number(newItem.unitCost) || 0)
      };

      if (editingId) {
        await updateDoc(doc(db, 'schools', activeSchoolId, 'inventory', editingId), payload);
      } else {
        await addDoc(collection(db, 'schools', activeSchoolId, 'inventory'), {
          ...ownerStamp(),
          ...payload,
          schoolId: activeSchoolId,
          lastStocktake: null
        });
      }
      
      setNewItem({ id: '', name: '', category: '', quantity: 1, assignedTo: '', location: '', status: 'Active', unitCost: 0, fundingSource: '', committee: '' });
      setShowAddForm(false);
      setEditingId(null);
      fetchData();
    } catch (e) {
      alert(t('inventoryAdmin.saveFailed', { message: e.message }));
    }
  };

  const handleEditItem = (item) => {
    setNewItem({
      id: item.id || '',
      name: item.name || '',
      category: item.category || '',
      quantity: item.quantity || 1,
      assignedTo: item.assignedTo || '',
      location: item.location || '',
      status: item.status || 'Active',
      unitCost: item.unitCost || 0,
      fundingSource: item.fundingSource || '',
      committee: item.committee || ''
    });
    setEditingId(item.uid);
    setShowAddForm(true);
    setActiveTab('inventory');
  };

  const handleDeleteItem = async (uid) => {
    if (!window.confirm(t('inventoryAdmin.deleteConfirm'))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'inventory', uid));
      fetchData();
    } catch (e) {
      alert(t('inventoryAdmin.error', { message: e.message }));
    }
  };

  const handleApproveApplication = async (app) => {
    try {
      // Update application status
      await updateDoc(doc(db, 'schools', activeSchoolId, 'inventory_applications', app.uid), { status: 'Approved' });
      
      // If it was a write-off or replacement with missing/broken items, we should update the actual inventory status or quantity
      if (app.type === 'Write-off' || app.type === 'Report Discrepancy') {
        const itemDoc = inventory.find(i => i.id === app.inventoryId);
        if (itemDoc) {
          await updateDoc(doc(db, 'schools', activeSchoolId, 'inventory', itemDoc.uid), { 
            status: app.type === 'Write-off' ? 'Written Off' : 'Needs Review (Discrepancy)'
          });
        }
      }

      fetchData();
    } catch (e) {
      alert(t('inventoryAdmin.error', { message: e.message }));
    }
  };

  const handleRejectApplication = async (uid) => {
    try {
      await updateDoc(doc(db, 'schools', activeSchoolId, 'inventory_applications', uid), { status: 'Rejected' });
      fetchData();
    } catch (e) {
      alert(t('inventoryAdmin.error', { message: e.message }));
    }
  };

  const handleExportCSV = () => {
    const csvData = inventory.map(item => ({
      'Item ID': item.id || '',
      'Item Name': item.name || '',
      'Category': item.category || '',
      'Quantity': item.quantity || '',
      'Unit Cost': item.unitCost || '',
      'Assigned To': item.assignedTo || '',
      'Location': item.location || '',
      'Funding Source': item.fundingSource || '',
      'Committee': item.committee || '',
      'Status': item.status || 'Active'
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Master_Inventory_Export.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const parsedData = results.data;
        let successCount = 0;
        let errorCount = 0;

        for (const row of parsedData) {
          const id = row['Item ID']?.trim();
          const name = row['Item Name']?.trim();
          if (!name) continue;

          const itemData = {
            schoolId: activeSchoolId,
            name: name,
            category: row['Category']?.trim() || '',
            quantity: parseInt(row['Quantity'], 10) || 1,
            unitCost: parseFloat(row['Unit Cost']) || 0,
            assignedTo: (users.find(u => u.label === row['Assigned To']?.trim())?.accountId) || row['Assigned To']?.trim() || '',
            location: row['Location']?.trim() || '',
            fundingSource: row['Funding Source']?.trim() || '',
            committee: row['Committee']?.trim() || '',
            status: row['Status']?.trim() || 'Active',
          };
          itemData.totalCost = itemData.quantity * itemData.unitCost;

          try {
            if (id) {
              const existingItem = inventory.find(i => i.id === id);
              if (existingItem) {
                // Update
                itemData.id = id;
                await updateDoc(doc(db, 'schools', activeSchoolId, 'inventory', existingItem.uid), itemData);
              } else {
                // Insert with provided ID
                itemData.id = id;
                itemData.lastStocktake = null;
                await addDoc(collection(db, 'schools', activeSchoolId, 'inventory'), { ...itemData, ...ownerStamp() });
              }
            } else {
              // Generate new ID
              itemData.id = `INV-${Date.now().toString().slice(-6)}-${Math.floor(Math.random() * 100)}`;
              itemData.lastStocktake = null;
              await addDoc(collection(db, 'schools', activeSchoolId, 'inventory'), { ...itemData, ...ownerStamp() });
            }
            successCount++;
          } catch (err) {
            console.error("Error importing row:", row, err);
            errorCount++;
          }
        }
        
        alert(t('inventoryAdmin.imported', { ok: successCount, failed: errorCount }));
        fetchData();
        e.target.value = null; // Reset input
      },
      error: (err) => {
        alert(t('inventoryAdmin.parseFailed', { message: err.message }));
      }
    });
  };

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%' }}>
      
      {/* Tabs */}
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {[
            { id: 'inventory', label: t('inventoryAdmin.tabs.master'), icon: Box },
            { id: 'applications', label: t('inventoryAdmin.tabs.applications'), icon: ClipboardList },
            { id: 'settings', label: t('inventoryAdmin.tabs.settings'), icon: Shield }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${isActive ? 'active' : ''}`}
              >
                <div className="theme-tab-icon">
                  <Icon size={20} />
                </div>
                {tab.label}
              </button>
            )
          })}
        </div>
        
        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '1rem', padding: '0.5rem 0' }}>
          {activeTab === 'inventory' && (
            <>
              <button onClick={handleExportCSV} title={t('inventoryAdmin.exportCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 1rem', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--color-text-main)' }}>
                <Download size={16} />
              </button>
              <label title={t('inventoryAdmin.importCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 1rem', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--color-text-main)', margin: 0 }}>
                <Upload size={16} />
                <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportCSV} />
              </label>
              <button className="btn-primary" onClick={() => setShowAddForm(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Plus size={16} /> Add Item
              </button>
            </>
          )}
          <button onClick={fetchData} className="btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
        </HeaderActionsPortal>
      </div>

      {/* Add/Edit Item Dialog -- a modal rather than an inline panel so opening it never reflows or
          pushes down the inventory table behind it. */}
      {showAddForm && (
        <div
          onClick={resetForm}
          style={{
            position: 'fixed', inset: 0, zIndex: 120,
            backgroundColor: 'var(--color-overlay)',
            display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
            padding: isMobile ? 0 : '2rem',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--color-bg-card)',
              borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              boxShadow: 'var(--shadow-lg)',
              width: '100%',
              maxWidth: isMobile ? '100%' : '900px',
              maxHeight: isMobile ? '92vh' : '90vh',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>
                {editingId ? t('inventoryAdmin.editItem') : t('inventoryAdmin.registerItem')}
              </h3>
              <button type="button" onClick={resetForm} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}>
                <X size={20} />
              </button>
            </div>
          <form onSubmit={handleAddItem} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryAdmin.itemIdOptional')}</label>
              <input type="text" className="input-field" value={newItem.id} onChange={e => setNewItem({...newItem, id: e.target.value})} placeholder={t('inventoryAdmin.itemIdPlaceholder')} disabled={!!editingId} />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryAdmin.itemName')}</label>
              <input type="text" className="input-field" value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} placeholder={t('inventoryAdmin.itemNamePlaceholder')} required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryAdmin.category')}</label>
              <select className="input-field" value={newItem.category} onChange={e => setNewItem({...newItem, category: e.target.value})} required>
                <option value="" disabled>-- Select Category --</option>
                {(dropdownConfig.inventoryCategory || []).filter(c => c.active !== false).map(c => {
                  const val = typeof c === 'string' ? c : (c.code || c.nameEn || c.name);
                  const label = typeof c === 'string' ? c : (c.code ? `${c.code} - ${c.nameEn || c.name}` : (c.nameEn || c.name));
                  return <option key={val} value={val}>{label}</option>;
                })}
              </select>
            </div>
            <div style={{ width: '100px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryAdmin.qty')}</label>
              <input type="number" className="input-field" value={newItem.quantity} onChange={e => setNewItem({...newItem, quantity: e.target.value})} min="1" required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryAdmin.assignedTo')}</label>
              <select className="input-field" value={newItem.assignedTo} onChange={e => setNewItem({...newItem, assignedTo: e.target.value})} required>
                <option value="" disabled>-- Select User/Initial --</option>
                {users.map(u => <option key={u.accountId} value={u.accountId}>{u.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryAdmin.location')}</label>
              <select className="input-field" value={newItem.location} onChange={e => setNewItem({...newItem, location: e.target.value})} required>
                <option value="" disabled>-- Select Location --</option>
                {(dropdownConfig.locations || []).filter(l => l.active !== false).map(l => (
                  <option key={l.code} value={l.code}>{l.code} - {l.nameEn || l.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryAdmin.committee')}</label>
              <select className="input-field" value={newItem.committee} onChange={e => setNewItem({...newItem, committee: e.target.value})} required>
                <option value="" disabled>-- Select Committee --</option>
                {(dropdownConfig.inventoryCommittee || []).filter(c => c.active !== false).map(c => {
                  const val = typeof c === 'string' ? c : (c.code || c.nameEn || c.name);
                  const label = typeof c === 'string' ? c : (c.code ? `${c.code} - ${c.nameEn || c.name}` : (c.nameEn || c.name));
                  return <option key={val} value={val}>{label}</option>;
                })}
              </select>
            </div>
            <div style={{ width: '120px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryAdmin.unitCostForm')}</label>
              <input type="number" className="input-field" value={newItem.unitCost} onChange={e => setNewItem({...newItem, unitCost: e.target.value})} min="0" required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryAdmin.fundingSource')}</label>
              <select className="input-field" value={newItem.fundingSource} onChange={e => setNewItem({...newItem, fundingSource: e.target.value})} required>
                <option value="" disabled>-- Select Funding --</option>
                {(dropdownConfig.sourceOfFunding || []).filter(f => f.active !== false).map(f => {
                  const val = typeof f === 'string' ? f : (f.code || f.nameEn || f.name);
                  const label = typeof f === 'string' ? f : (f.code ? `${f.code} - ${f.nameEn || f.name}` : (f.nameEn || f.name));
                  return <option key={val} value={val}>{label}</option>;
                })}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryAdmin.status')}</label>
              <select className="input-field" value={newItem.status} onChange={e => setNewItem({...newItem, status: e.target.value})} required>
                <option value="Active">{statusWord('Active')}</option>
                <option value="Inactive">{statusWord('Inactive')}</option>
                <option value="Written Off">{statusWord('Written Off')}</option>
                <option value="Needs Repair">{statusWord('Needs Repair')}</option>
                <option value="Needs Review (Discrepancy)">{t('inventoryAdmin.needsReviewShort')}</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
               <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('inventoryAdmin.totalCost')}</label>
               <div style={{ fontWeight: '700', fontSize: '1.1rem', color: 'var(--color-primary)' }}>
                 ${ ((Number(newItem.quantity) || 0) * (Number(newItem.unitCost) || 0)).toLocaleString() }
               </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
              <button type="button" className="btn-outline" onClick={resetForm}>{t('common.cancel')}</button>
              <button type="submit" className="btn-primary">{t('inventoryAdmin.saveItem')}</button>
            </div>
          </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ flex: 1, backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: 'var(--color-text-muted)' }}>
            <RefreshCw className="animate-spin" size={24} style={{ marginRight: '0.5rem' }} /> Loading Inventory Data...
          </div>
        ) : activeTab === 'inventory' ? (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)' }}>
                <tr>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.itemId')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.itemName')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.category')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.qty')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.assignedTo')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.location')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.committee')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.funding')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.costTotal')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.status')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', textAlign: 'right' }}>{t('inventoryAdmin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {inventory.length === 0 ? <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.noInventory')}</td></tr> :
                inventory.map(i => (
                  <tr key={i.uid} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '1rem', fontFamily: 'monospace', color: 'var(--color-text-muted)' }}>{i.id}</td>
                    <td style={{ padding: '1rem', fontWeight: '600' }}>{i.name}</td>
                    <td style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>{i.category}</td>
                    <td style={{ padding: '1rem', fontWeight: '600' }}>{i.quantity}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{userMap[i.assignedTo] || i.assignedTo}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>{i.location}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>{i.committee || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>{i.fundingSource}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      ${i.unitCost?.toLocaleString()} <br/>
                      <span style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>(Total: ${i.totalCost?.toLocaleString()})</span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600',
                        backgroundColor: i.status === 'Active' ? 'var(--color-success-bg)' : 'var(--color-danger-bg)',
                        color: i.status === 'Active' ? 'var(--color-success-text)' : 'var(--color-danger-text)'
                      }}>
                        {i.status}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <button onClick={() => handleEditItem(i)} className="btn-outline" style={{ padding: '0.4rem', marginRight: '0.5rem' }}><Edit2 size={16}/></button>
                      <button onClick={() => handleDeleteItem(i.uid)} className="btn-outline" style={{ padding: '0.4rem', color: 'var(--color-danger)' }}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : activeTab === 'settings' ? (
          <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 0 : '1.5rem' }}>
            <ModuleEditPermissions
              settingsDoc="inventory_settings"
              moduleId="inventory_admin"
              recordLabel="the inventory catalogue"
              approveLabel="approve equipment requests"
            />
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)' }}>
                <tr>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.type')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.item')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.user')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.remarks')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.status')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', textAlign: 'right' }}>{t('inventoryAdmin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {applications.length === 0 ? <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('inventoryAdmin.noApplications')}</td></tr> :
                applications.map(a => (
                  <tr key={a.uid} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '1rem', fontWeight: '600' }}>{appTypeWord(a.type)}</td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontWeight: '600' }}>{a.itemName}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontFamily: 'monospace' }}>{a.inventoryId}</div>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{userMap[a.submittedBy] || a.submittedBy}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)', maxWidth: '200px' }}>
                      {a.remarks}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600',
                        backgroundColor: a.status === 'Approved' ? 'var(--color-success-bg)' : a.status === 'Pending' ? 'var(--color-warning-bg)' : 'var(--color-danger-bg)',
                        color: a.status === 'Approved' ? 'var(--color-success-text)' : a.status === 'Pending' ? 'var(--color-warning-text)' : 'var(--color-danger-text)'
                      }}>
                        {appStatusWord(a.status)}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      {a.status === 'Pending' && (
                        <>
                          <button onClick={() => handleApproveApplication(a)} className="btn-primary" style={{ padding: '0.4rem', fontSize: '0.75rem', backgroundColor: 'var(--color-success)' }}>
                            {t('inventoryAdmin.approve')}
                          </button>
                          <button onClick={() => handleRejectApplication(a.uid)} className="btn-primary" style={{ padding: '0.4rem', fontSize: '0.75rem', backgroundColor: 'var(--color-danger)' }}>
                            {t('inventoryAdmin.reject')}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
};

export default InventoryAdmin;
